import { CountryLike } from "./countrySearch";

export type RegionKey = "AMERICA" | "EUROPE" | "ASIA_PACIFIC" | "MEA";

export interface Region {
  key: RegionKey;
  label: string;
}

// Display order of regions in country lists. Intentionally NOT alphabetical so
// that Afghanistan (and other A* countries) no longer sit at the very top.
export const REGIONS: Region[] = [
  { key: "AMERICA", label: "Americas" },
  { key: "EUROPE", label: "Europe" },
  { key: "ASIA_PACIFIC", label: "Asia Pacific" },
  { key: "MEA", label: "Middle East & Africa" },
];

// Label used for any country code that is missing from COUNTRY_REGION below.
export const OTHER_REGION_LABEL = "Other";

// ISO 3166-1 alpha-2 code -> region. Based on the UN geoscheme, adapted to the
// four marketing regions used by the team. Central Asia and the Caucasus are
// kept with Asia Pacific / Europe respectively; the Greater Middle East
// (incl. Afghanistan) sits in MEA.
export const COUNTRY_REGION: Record<string, RegionKey> = {
  // ── Америка (North / Central / South America + Caribbean) ──
  AG: "AMERICA",
  AI: "AMERICA",
  AR: "AMERICA",
  AW: "AMERICA",
  BB: "AMERICA",
  BL: "AMERICA",
  BM: "AMERICA",
  BO: "AMERICA",
  BQ: "AMERICA",
  BR: "AMERICA",
  BS: "AMERICA",
  BZ: "AMERICA",
  CA: "AMERICA",
  CL: "AMERICA",
  CO: "AMERICA",
  CR: "AMERICA",
  CU: "AMERICA",
  CW: "AMERICA",
  DM: "AMERICA",
  DO: "AMERICA",
  EC: "AMERICA",
  FK: "AMERICA",
  GD: "AMERICA",
  GF: "AMERICA",
  GL: "AMERICA",
  GP: "AMERICA",
  GS: "AMERICA",
  GT: "AMERICA",
  GY: "AMERICA",
  HN: "AMERICA",
  HT: "AMERICA",
  JM: "AMERICA",
  KN: "AMERICA",
  KY: "AMERICA",
  LC: "AMERICA",
  MF: "AMERICA",
  MQ: "AMERICA",
  MS: "AMERICA",
  MX: "AMERICA",
  NI: "AMERICA",
  PA: "AMERICA",
  PE: "AMERICA",
  PM: "AMERICA",
  PR: "AMERICA",
  PY: "AMERICA",
  SR: "AMERICA",
  SV: "AMERICA",
  SX: "AMERICA",
  TC: "AMERICA",
  TT: "AMERICA",
  UM: "AMERICA",
  US: "AMERICA",
  UY: "AMERICA",
  VC: "AMERICA",
  VE: "AMERICA",
  VG: "AMERICA",
  VI: "AMERICA",

  // ── Европа (Europe + Caucasus + western CIS) ──
  AD: "EUROPE",
  AL: "EUROPE",
  AM: "EUROPE",
  AT: "EUROPE",
  AX: "EUROPE",
  AZ: "EUROPE",
  BA: "EUROPE",
  BE: "EUROPE",
  BG: "EUROPE",
  BY: "EUROPE",
  CH: "EUROPE",
  CY: "EUROPE",
  CZ: "EUROPE",
  DE: "EUROPE",
  DK: "EUROPE",
  EE: "EUROPE",
  ES: "EUROPE",
  FI: "EUROPE",
  FO: "EUROPE",
  FR: "EUROPE",
  GB: "EUROPE",
  GE: "EUROPE",
  GG: "EUROPE",
  GI: "EUROPE",
  GR: "EUROPE",
  HR: "EUROPE",
  HU: "EUROPE",
  IE: "EUROPE",
  IM: "EUROPE",
  IS: "EUROPE",
  IT: "EUROPE",
  JE: "EUROPE",
  LI: "EUROPE",
  LT: "EUROPE",
  LU: "EUROPE",
  LV: "EUROPE",
  MC: "EUROPE",
  MD: "EUROPE",
  ME: "EUROPE",
  MK: "EUROPE",
  MT: "EUROPE",
  NL: "EUROPE",
  NO: "EUROPE",
  PL: "EUROPE",
  PT: "EUROPE",
  RO: "EUROPE",
  RS: "EUROPE",
  RU: "EUROPE",
  SE: "EUROPE",
  SI: "EUROPE",
  SJ: "EUROPE",
  SK: "EUROPE",
  SM: "EUROPE",
  TR: "EUROPE",
  UA: "EUROPE",
  VA: "EUROPE",

  // ── Азия Пацифик (East / SE / South / Central Asia + Oceania) ──
  AQ: "ASIA_PACIFIC",
  AS: "ASIA_PACIFIC",
  AU: "ASIA_PACIFIC",
  BD: "ASIA_PACIFIC",
  BN: "ASIA_PACIFIC",
  BT: "ASIA_PACIFIC",
  CC: "ASIA_PACIFIC",
  CK: "ASIA_PACIFIC",
  CN: "ASIA_PACIFIC",
  CX: "ASIA_PACIFIC",
  FJ: "ASIA_PACIFIC",
  FM: "ASIA_PACIFIC",
  GU: "ASIA_PACIFIC",
  HK: "ASIA_PACIFIC",
  HM: "ASIA_PACIFIC",
  ID: "ASIA_PACIFIC",
  IN: "ASIA_PACIFIC",
  IO: "ASIA_PACIFIC",
  JP: "ASIA_PACIFIC",
  KG: "ASIA_PACIFIC",
  KH: "ASIA_PACIFIC",
  KI: "ASIA_PACIFIC",
  KP: "ASIA_PACIFIC",
  KR: "ASIA_PACIFIC",
  KZ: "ASIA_PACIFIC",
  LA: "ASIA_PACIFIC",
  LK: "ASIA_PACIFIC",
  MH: "ASIA_PACIFIC",
  MM: "ASIA_PACIFIC",
  MN: "ASIA_PACIFIC",
  MO: "ASIA_PACIFIC",
  MP: "ASIA_PACIFIC",
  MV: "ASIA_PACIFIC",
  MY: "ASIA_PACIFIC",
  NC: "ASIA_PACIFIC",
  NF: "ASIA_PACIFIC",
  NP: "ASIA_PACIFIC",
  NR: "ASIA_PACIFIC",
  NU: "ASIA_PACIFIC",
  NZ: "ASIA_PACIFIC",
  PF: "ASIA_PACIFIC",
  PG: "ASIA_PACIFIC",
  PH: "ASIA_PACIFIC",
  PK: "ASIA_PACIFIC",
  PN: "ASIA_PACIFIC",
  PW: "ASIA_PACIFIC",
  SB: "ASIA_PACIFIC",
  SG: "ASIA_PACIFIC",
  TF: "ASIA_PACIFIC",
  TH: "ASIA_PACIFIC",
  TJ: "ASIA_PACIFIC",
  TK: "ASIA_PACIFIC",
  TL: "ASIA_PACIFIC",
  TM: "ASIA_PACIFIC",
  TO: "ASIA_PACIFIC",
  TV: "ASIA_PACIFIC",
  TW: "ASIA_PACIFIC",
  UZ: "ASIA_PACIFIC",
  VN: "ASIA_PACIFIC",
  VU: "ASIA_PACIFIC",
  WF: "ASIA_PACIFIC",
  WS: "ASIA_PACIFIC",

  // ── Мидл Ист и Африка (Middle East + Africa) ──
  AE: "MEA",
  AF: "MEA",
  AO: "MEA",
  BF: "MEA",
  BH: "MEA",
  BI: "MEA",
  BJ: "MEA",
  BV: "MEA",
  BW: "MEA",
  CD: "MEA",
  CF: "MEA",
  CG: "MEA",
  CI: "MEA",
  CM: "MEA",
  CV: "MEA",
  DJ: "MEA",
  DZ: "MEA",
  EG: "MEA",
  EH: "MEA",
  ER: "MEA",
  ET: "MEA",
  GA: "MEA",
  GH: "MEA",
  GM: "MEA",
  GN: "MEA",
  GQ: "MEA",
  GW: "MEA",
  IL: "MEA",
  IQ: "MEA",
  IR: "MEA",
  JO: "MEA",
  KE: "MEA",
  KM: "MEA",
  KW: "MEA",
  LB: "MEA",
  LR: "MEA",
  LS: "MEA",
  LY: "MEA",
  MA: "MEA",
  MG: "MEA",
  ML: "MEA",
  MR: "MEA",
  MU: "MEA",
  MW: "MEA",
  MZ: "MEA",
  NA: "MEA",
  NE: "MEA",
  NG: "MEA",
  OM: "MEA",
  PS: "MEA",
  QA: "MEA",
  RE: "MEA",
  RW: "MEA",
  SA: "MEA",
  SC: "MEA",
  SD: "MEA",
  SH: "MEA",
  SL: "MEA",
  SN: "MEA",
  SO: "MEA",
  SS: "MEA",
  ST: "MEA",
  SY: "MEA",
  SZ: "MEA",
  TD: "MEA",
  TG: "MEA",
  TN: "MEA",
  TZ: "MEA",
  UG: "MEA",
  YE: "MEA",
  YT: "MEA",
  ZA: "MEA",
  ZM: "MEA",
  ZW: "MEA",
};

const getCode = (item: CountryLike): string =>
  (item.value ?? item.code ?? "").toUpperCase();

const getLabel = (item: CountryLike): string => item.label ?? item.name ?? "";

export const getCountryRegion = (item: CountryLike): RegionKey | null =>
  COUNTRY_REGION[getCode(item)] ?? null;

export interface CountryRegionGroup<T extends CountryLike> {
  key: RegionKey | "OTHER";
  label: string;
  countries: T[];
}

/**
 * Splits a flat country list into ordered regional groups. Regions follow the
 * REGIONS order (so Afghanistan no longer leads the list); within each region
 * countries stay alphabetical. Any unmapped code lands in a trailing "Другое"
 * group so nothing is ever dropped. Empty groups are omitted.
 */
export const groupCountriesByRegion = <T extends CountryLike>(
  data: T[],
): CountryRegionGroup<T>[] => {
  const buckets = new Map<RegionKey | "OTHER", T[]>();

  data.forEach((item) => {
    const key = getCountryRegion(item) ?? "OTHER";
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      buckets.set(key, [item]);
    }
  });

  const sortByLabel = (a: T, b: T) => getLabel(a).localeCompare(getLabel(b));

  const groups: CountryRegionGroup<T>[] = [];

  REGIONS.forEach((region) => {
    const countries = buckets.get(region.key);
    if (countries && countries.length > 0) {
      groups.push({
        key: region.key,
        label: region.label,
        countries: [...countries].sort(sortByLabel),
      });
    }
  });

  const other = buckets.get("OTHER");
  if (other && other.length > 0) {
    groups.push({
      key: "OTHER",
      label: OTHER_REGION_LABEL,
      countries: [...other].sort(sortByLabel),
    });
  }

  return groups;
};
